import React, { useContext } from 'react';
import { useSelector } from 'react-redux';
import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { ImageNext } from '@carvana/purchase-helpers';
import styled, { css } from 'styled-components';
import { CarvanaTheme, getMediaQuery } from '@carvana/showroom-theme';

type VehicleState = {
  vehicle: {
    year: number;
    make: string;
    model: string;
    evoxImageUrl: string;
    srcImageUrl: string;
    trim: string;
    vehicleId: string;
  };
};

const Container = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  padding: 28px 30px;
  position: relative;

  ${getMediaQuery(CarvanaTheme.breakpoint.md)} {
    cursor: pointer;
  }
`;

const imageSize = '65px';

const ImageContainer = styled.div`
  align-items: center;
  background-color: gray;
  border-radius: 50%;
  display: flex;
  height: ${imageSize};
  justify-content: center;
  margin-right: 16px;
  overflow: hidden;
  width: ${imageSize};
`;

const imageStyles = css`
  height: 100%;
  transform: translate(10px, 0px) scale(1.3);
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  color: ${props => props.theme.carvana.blue.dark};
  max-width: 168px;
  gap: 4px;

  ${getMediaQuery(CarvanaTheme.breakpoint.xl)} {
    max-width: 180px;
  }

  ${getMediaQuery(CarvanaTheme.breakpoint.xxl)} {
    max-width: 210px;
  }
`;

const Text = styled.p`
  display: block;
  font-size: 14px;
`;

const YearMakeModelText = styled(Text)`
  font-size: 16px;
  line-height: 20px;
  font-weight: bold;
`;

const TrimText = styled(Text)`
  font-size: 14px;
  line-height: 20px;
`;

const VdpLink = styled(Text)`
  font-size: 14px;
  line-height: 20px;
  cursor: pointer;
  color: ${props => props.theme.carvana.blue.primary};
`;

const VehicleSnapshot: React.FC = () => {
  const vehicle = useSelector((state: VehicleState) => state?.vehicle);
  const yearMakeModelText = `${vehicle.year} ${vehicle.make} ${vehicle.model}`;
  const isNativeWebview = /CarvanaNativeWebView/gi.test(navigator?.userAgent);
  const { trackEvent } = useContext(AnalyticsContext);

  return (
    <Container
      onClick={() => {
        if (!isNativeWebview) {
          trackEvent('pp_clicked_vdp');
          window.open(`/vehicle/${vehicle.vehicleId}`, '_self');
        }
      }}
    >
      <ImageContainer>
        <ImageNext
          alt={`${yearMakeModelText}`}
          fallbackSrc={vehicle.evoxImageUrl}
          src={vehicle.srcImageUrl}
          styles={imageStyles}
        />
      </ImageContainer>
      <TextContainer>
        <YearMakeModelText>{yearMakeModelText}</YearMakeModelText>
        <TrimText>{vehicle.trim}</TrimText>
        {!isNativeWebview && <VdpLink>View vehicle details</VdpLink>}
      </TextContainer>
    </Container>
  );
};

export default VehicleSnapshot;
